import { useCallback, useState } from 'react'

import styles from './LayeredWallpaper.module.scss'
import type { LayeredWallpaperProps } from './LayeredWallpaper.types'
import { ParticleLayer } from './ParticleLayer/ParticleLayer'
import { WallpaperLayer } from './WallpaperLayer/WallpaperLayer'
import { WaterLayer } from './WaterLayer/WaterLayer'

export const LayeredWallpaper = ({ definition }: LayeredWallpaperProps) => {
  const [loaded, setLoaded] = useState<Set<string>>(() => new Set())
  const [failed, setFailed] = useState(false)
  const onLoaded = useCallback((id: string) => {
    setLoaded(current =>
      current.has(id) ? current : new Set([...current, id])
    )
  }, [])
  const onFailed = useCallback(() => setFailed(true), [])
  const ready = definition.layers.every(
    layer => layer.type === 'gradient' || loaded.has(layer.id)
  )
  const ratio = definition.canvas.width / definition.canvas.height
  const fit = definition.canvas.fit === 'contain' ? 'min' : 'max'

  if (failed) return null

  return (
    <div
      className={styles.scene}
      style={{ backgroundColor: definition.backgroundColor }}
      data-ready={ready}
    >
      {definition.layers.map((layer, index) => (
        <div
          key={layer.id}
          className={styles.coordinateSpace}
          style={
            layer.layout.space === 'artwork'
              ? {
                  width: `${fit}(100%, ${ratio * 100}cqh)`,
                  aspectRatio: ratio,
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: index
                }
              : { inset: 0, zIndex: index }
          }
        >
          {layer.type === 'water' ? (
            <WaterLayer
              layer={layer}
              assetBaseUrl={definition.assetBaseUrl}
              ready={ready}
              onLoaded={onLoaded}
              onFailed={onFailed}
            />
          ) : layer.type === 'particles' ? (
            <ParticleLayer
              layer={layer}
              assetBaseUrl={definition.assetBaseUrl}
              ready={ready}
              onLoaded={onLoaded}
              onFailed={onFailed}
            />
          ) : (
            <WallpaperLayer
              layer={layer}
              assetBaseUrl={definition.assetBaseUrl}
              ready={ready}
              onLoaded={onLoaded}
              onFailed={onFailed}
            />
          )}
        </div>
      ))}
    </div>
  )
}
